<template>
  <!-- The hike page's folded "N warnings on route" section, under the stats
       row. The server draws the folded box with its count (the same markup as
       this, styled by styles/_route_warnings.scss), so nothing moves when this
       mounts over it; the rows load in the background straight away, and a
       box opened before they arrive shows a loader until they do. Nothing at
       all at 0. Without a server count (it couldn't work one out) the box
       appears once the rows have loaded, as it used to.
       A div, not <section>: app.scss gives every section a 4rem !important
       bottom margin, which would leave a gap under the box. -->
  <div v-if="count > 0" ref="section" class="route-warnings" data-test="route-warnings">
    <button
      type="button"
      class="route-warnings__header"
      :aria-expanded="expanded ? 'true' : 'false'"
      aria-controls="route-warnings-list"
      data-test="route-warnings-toggle"
      @click="toggle"
    >
      <span class="material-icons route-warnings__icon" aria-hidden="true">warning</span>
      <span class="route-warnings__title" data-test="route-warnings-title">{{ title }}</span>
      <span class="material-icons route-warnings__chevron" aria-hidden="true">{{ expanded ? 'expand_less' : 'expand_more' }}</span>
    </button>
    <div v-show="expanded" id="route-warnings-list" class="route-warnings__body">
      <!-- Every row opens the hike's map on that warning (the page's own map is
           a static picture). Without the map's address, sections are plain rows
           and a hazard opens its details here. -->
      <div v-if="expanded && !loaded && !failed" class="route-warnings__status" role="status" data-test="route-warnings-loading">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>{{ $t('shared.loading') }}</span>
      </div>
      <p v-else-if="expanded && failed" class="route-warnings__status" role="alert" data-test="route-warnings-error">
        {{ $t('trail_hazards.error_generic') }}
      </p>
      <RouteAlertList v-else-if="expanded" :rows="rows" :interactive-sections="!!mapUrl" @select="onSelect" />
    </div>
  </div>
  <!-- No map on this page: the pager only swaps the details (no camera, no ring). -->
  <HazardDetailsModal
    ref="details"
    screen="hike_overview"
    modal
    :pager="hazardPagerView"
    @gone="reload"
    @close="onDetailsClosed"
    @pager-previous="stepHazard('previous')"
    @pager-next="stepHazard('next')"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import RouteAlertList from './components/RouteAlertList.vue';
import HazardDetailsModal from '../trail_hazards/HazardDetailsModal.vue';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { HikeRouteAlertsLoader } from '../../utils/routeAlerts/routeAlertsLoader';
import {
  EMPTY_ROUTE_ALERTS,
  buildRouteAlertCardRows,
  warningsOnRouteText,
} from '../../utils/routeAlerts/routeAlertsModel';
import type { RouteAlertRowView, RouteAlerts } from '../../utils/routeAlerts/routeAlertsModel';
import {
  routeAlertPagedEvent,
  routeAlertTappedEvent,
  routeAlertsOpenedEvent,
  routeAlertsShownEvent,
  trackRouteAlertsEvent,
} from '../../utils/routeAlerts/routeAlertsAnalytics';
import { currentAlert, pagerFor, pagerView, refreshPager, stepPager } from '../../utils/routeAlerts/routeAlertGroups';
import { routeAlertMapUrl } from '../../utils/routeAlerts/warningsLink';
import type { PagerDirection, PagerView, RouteAlertPager } from '../../utils/routeAlerts/routeAlertGroups';

const SCREEN = 'hike_overview';

export default defineComponent({
  name: 'HikeRouteWarnings',
  components: { RouteAlertList, HazardDetailsModal },
  props: {
    hikeId: { type: String, required: true },
    /** The hike's map page: a warning opens it, focused on that warning. */
    mapUrl: { type: String, default: null },
    /** Goes to a URL (the browser's own navigation; replaced in tests). */
    navigate: {
      type: Function as PropType<(url: string) => void>,
      default: (url: string) => window.location.assign(url),
    },
    /** The viewer's units: km (metric) or miles for "at X km". */
    metric: { type: Boolean, default: true },
    /** Opened from the map card's chip (#warnings): expand and scroll to it. */
    openOnLoad: { type: Boolean, default: false },
    /** The count the server drew the folded box with; null when it had none. */
    initialCount: { type: Number as PropType<number | null>, default: null },
  },
  data() {
    return {
      alerts: EMPTY_ROUTE_ALERTS as RouteAlerts,
      /** The rows have arrived (or the route turned out to have none). */
      loaded: false,
      /** The last load failed; opening the box again tries again. */
      failed: false,
      expanded: false,
      shownTracked: false,
      /** The hazard details' pager through the card's group (‹ 1 / 3 ›); null for none. */
      hazardPager: null as RouteAlertPager | null,
    };
  },
  computed: {
    /** The loaded count once the rows are in; the server's until then. */
    count(): number {
      if (this.loaded) return this.alerts.count;
      return this.initialCount ?? 0;
    },
    rows(): RouteAlertRowView[] {
      const t = (key: string, values?: Record<string, unknown> | number) => this.$t(key, values ?? {}) as string;
      return buildRouteAlertCardRows(this.alerts, { metric: this.metric, locale: this.$i18n?.locale }, t);
    },
    title(): string {
      return warningsOnRouteText(this.count, (key, values) => this.$t(key, values ?? {}) as string);
    },
    hazardPagerView(): PagerView | null {
      return pagerView(this.hazardPager, (key, values) => this.$t(key, values ?? {}) as string);
    },
  },
  created() {
    (this as any).loader = new HikeRouteAlertsLoader(graph_client as any, reportHandledError, SCREEN);
  },
  mounted() {
    // The box is already on the page: one opened from the map card's chip opens
    // now, with the loader, rather than after the rows arrive.
    if (this.openOnLoad && this.count > 0) this.openFromLink();
    this.load(true);
  },
  beforeUnmount() {
    (this as any).loader.cancel();
  },
  methods: {
    async load(first = false) {
      this.failed = false;
      const result = await (this as any).loader.load(this.hikeId);
      if (result.kind === 'stale') return;
      if (result.kind === 'failed' && this.initialCount && !this.loaded) {
        // The server counted some: keep the box, say so if it's open, and try
        // again the next time it's opened.
        this.failed = true;
        return;
      }
      this.alerts = result.kind === 'loaded' ? result.alerts : EMPTY_ROUTE_ALERTS;
      this.loaded = true;
      // The open hazard's group, as it now stands (no pager once it is alone).
      this.hazardPager = refreshPager(this.hazardPager, this.alerts.alerts);
      if (this.alerts.count > 0 && !this.shownTracked) {
        this.shownTracked = true;
        trackRouteAlertsEvent(routeAlertsShownEvent(this.alerts, SCREEN));
      }
      if (first && this.openOnLoad && this.alerts.count > 0 && !this.expanded) this.openFromLink();
    },

    /** Opened from the map card's chip (#warnings): expanded and brought into view. */
    openFromLink() {
      this.expanded = true;
      this.$nextTick(() => this.scrollIntoView());
    },

    /** A hazard left the map since the list loaded: fetch the list again. */
    reload() {
      this.load();
    },

    toggle() {
      this.expanded = !this.expanded;
      if (!this.expanded) return;
      trackRouteAlertsEvent(routeAlertsOpenedEvent(this.count, SCREEN));
      if (this.failed) this.load();
    },

    /**
     * A row: the hike's map, focused on that warning (a hazard with its details
     * and pager, a section framed with its popup). Without the map's address a
     * hazard opens its details here, and section rows aren't buttons.
     */
    onSelect(row: RouteAlertRowView) {
      const onMap = routeAlertMapUrl(this.mapUrl, row.alert.id);
      if (row.sectionId && !onMap) return;
      trackRouteAlertsEvent(routeAlertTappedEvent(row.alert, SCREEN, row.moreCount));
      if (onMap) {
        this.openMap(onMap);
        return;
      }
      if (!row.hazardId) return;
      this.hazardPager = pagerFor(this.alerts.alerts, row.alert);
      (this.$refs.details as any)?.open(row.hazardId);
    },

    openMap(url: string) {
      this.navigate(url);
    },

    /** An arrow of the details' pager: the next hazard of the card's group. */
    stepHazard(direction: PagerDirection) {
      const next = stepPager(this.hazardPager, direction);
      const alert = currentAlert(next);
      if (!next || !alert?.hazard) return;
      this.hazardPager = next;
      (this.$refs.details as any)?.open(alert.hazard.id, { keepDetails: true });
      trackRouteAlertsEvent(routeAlertPagedEvent(alert, SCREEN, next.index + 1, next.members.length, direction));
    },

    onDetailsClosed() {
      this.hazardPager = null;
    },

    scrollIntoView() {
      const section = this.$refs.section as HTMLElement | undefined;
      try {
        section?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } catch {
        // Old browsers: the #warnings anchor already brought the page close.
      }
    },
  },
});
</script>
